import { useMemo, useState } from "react";
import { NumField } from "@/components/tool/CalcFields";
import { masterMix, type PcrComponent } from "@/lib/science/pcr";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

const DEFAULTS: PcrComponent[] = [
  { name: "10X reaction buffer", perRxn: 2.5 },
  { name: "dNTP mix (10 mM)", perRxn: 0.5 },
  { name: "Forward primer (10 µM)", perRxn: 0.5 },
  { name: "Reverse primer (10 µM)", perRxn: 0.5 },
  { name: "Taq polymerase", perRxn: 0.125 },
];

export function PcrMasterMix() {
  const locale = getLocale();
  const [comps, setComps] = useState<PcrComponent[]>(DEFAULTS);
  const [reactions, setReactions] = useState("10");
  const [overage, setOverage] = useState("10");

  const result = useMemo(
    () => masterMix(comps, Number(reactions), Number(overage) / 100),
    [comps, reactions, overage],
  );

  const setPerRxn = (i: number, v: string) =>
    setComps((cs) =>
      cs.map((c, idx) => (idx === i ? { ...c, perRxn: Number(v) } : c)),
    );

  const num = (n: number) =>
    n.toLocaleString(locale, { maximumFractionDigits: 2 });

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <NumField
          id="reactions"
          label={m.pcr_reactions()}
          value={reactions}
          onChange={setReactions}
        />
        <NumField
          id="overage"
          label={m.pcr_overage()}
          value={overage}
          onChange={setOverage}
          suffix="%"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="pb-2 font-medium">{m.pcr_component()}</th>
              <th className="pb-2 text-right font-medium">{m.pcr_per_rxn()}</th>
              <th className="pb-2 text-right font-medium">{m.pcr_total()}</th>
            </tr>
          </thead>
          <tbody>
            {comps.map((c, i) => (
              <tr key={c.name} className="border-black/5 border-t">
                <td className="py-2 pr-2">{c.name}</td>
                <td className="py-2 text-right">
                  <input
                    type="number"
                    aria-label={`${c.name} ${m.pcr_per_rxn()}`}
                    value={c.perRxn}
                    onChange={(e) => setPerRxn(i, e.target.value)}
                    className="w-20 rounded-md border border-black/15 bg-white px-2 py-1 text-right text-neutral-900"
                  />
                </td>
                <td className="py-2 text-right font-mono">
                  {result ? `${num(result.rows[i].total)} µL` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {result ? (
        <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
          <span className="text-sm font-medium text-muted-foreground">
            {m.pcr_mm_total()}
          </span>
          <span
            data-testid="pcr-result"
            className="sci-result font-display text-3xl font-bold sm:text-4xl"
          >
            {num(result.totalVolume)} µL
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{m.pcr_hint()}</p>
      )}
      <p className="text-xs text-muted-foreground">
        {m.pcr_pertube({ vol: result ? num(result.perTube) : "—" })}
      </p>
    </div>
  );
}
